// Utils
import lineClamp from "../js/utils/line-clamp";

// Core (synchronous — above-the-fold, always needed on every page)
import Navigation from "./organisms/navigation/navigation";
import BannerAlert from "./organisms/banner-alert/banner-alert";
import Button from "./atoms/button/button";
import HomeHeader from "./organisms/home-header/home-header";
import MediaHeader from "./wp-blocks/media-header/media-header";
import PageHeader from "./organisms/page-header/page-header";
import ArtistHeader from "./organisms/artist-header/artist-header";

const SYNC_COMPONENTS = [
	{ view: Navigation, name: "Navigation", selector: "[data-navigation]" },
	{ view: BannerAlert, name: "BannerAlert", selector: "[data-banner-alert]" },
	{ view: Button, name: "Button", selector: "[data-button]" },
	{ view: HomeHeader, name: "HomeHeader", selector: "[data-home-header]" },
	{ view: MediaHeader, name: "MediaHeader", selector: "[data-media-header]" },
	{ view: PageHeader, name: "PageHeader", selector: "[data-page-header]" },
	{ view: ArtistHeader, name: "ArtistHeader", selector: "[data-artist-header]" },
];

// All other components are code-split — their modules are fetched only when
// their selector is found in the DOM.
const LAZY_COMPONENTS = [
	{ load: () => import(/* webpackChunkName: "c-ap-watch" */ "./organisms/ap-watch/ap-watch"), name: "ApWatch", selector: "[data-ap-watch]" },
	{ load: () => import(/* webpackChunkName: "c-responsive-video" */ "./2-javascript/responsive-video/responsive-video"), name: "ResponsiveVideo", selector: "[data-responsive-video]" },
	{ load: () => import(/* webpackChunkName: "c-programme" */ "./organisms/programme/programme"), name: "Programme", selector: "[data-programme]" },
	{ load: () => import(/* webpackChunkName: "c-artist-heritage" */ "./organisms/artist-heritage/artist-heritage"), name: "ArtistHeritage", selector: "[data-artist-heritage]" },
	{ load: () => import(/* webpackChunkName: "c-accordion" */ "./molecules/accordion/accordion"), name: "Accordion", selector: "[data-accordion]" },
	{ load: () => import(/* webpackChunkName: "c-article-tags" */ "./molecules/article-tags/article-tags"), name: "ArticleTags", selector: "[data-article-tags]" },
	{ load: () => import(/* webpackChunkName: "c-page-navigation" */ "./organisms/page-navigation/page-navigation"), name: "PageNavigation", selector: "[data-page-navigation]" },
	{ load: () => import(/* webpackChunkName: "c-highlighted-content" */ "./wp-blocks/highlighted-content/highlighted-content"), name: "HighlightedContent", selector: "[data-highlighted-content]" },
	{ load: () => import(/* webpackChunkName: "c-article-breaker" */ "./wp-blocks/article-breaker/article-breaker"), name: "ArticleBreaker", selector: "[data-article-breaker]" },
	{ load: () => import(/* webpackChunkName: "c-loadmore" */ "./2-javascript/loadmore/loadmore"), name: "Loadmore", selector: "[data-loadmore]" },
	{ load: () => import(/* webpackChunkName: "c-gallery" */ "./wp-blocks/gallery/gallery"), name: "Gallery", selector: "[data-gallery]" },
	{ load: () => import(/* webpackChunkName: "c-gallery-video" */ "./wp-blocks/gallery-video/gallery-video"), name: "GalleryVideo", selector: "[data-gallery-video]" },
	{ load: () => import(/* webpackChunkName: "c-breadcrumbs" */ "./molecules/breadcrumbs/breadcrumbs"), name: "Breadcrumbs", selector: "[data-breadcrumbs]" },
	{ load: () => import(/* webpackChunkName: "c-form" */ "./organisms/form/form"), name: "Form", selector: "[data-form]" },
	{ load: () => import(/* webpackChunkName: "c-video-player" */ "./molecules/video-player/video-player"), name: "VideoPlayer", selector: "[data-video]" },
	{ load: () => import(/* webpackChunkName: "c-page-media" */ "./templates/page/page-media"), name: "PageMedia", selector: "[data-page-media-content]" },
	{ load: () => import(/* webpackChunkName: "c-ud-nav" */ "./user-dashboard/ud-nav/ud-nav"), name: "UdNav", selector: ".ud-nav" },
	{ load: () => import(/* webpackChunkName: "c-user-dashboard" */ "./user-dashboard/user-dashboard"), name: "UserDashboard", selector: ".page--user-dashboard" },
	{ load: () => import(/* webpackChunkName: "c-section-text-media" */ "./organisms/sections/section-text-media/section-text-media"), name: "SectionTextMedia", selector: "[data-section-text-media]" },
	{ load: () => import(/* webpackChunkName: "c-cards-highlight" */ "./organisms/cards-highlight/cards-highlight"), name: "CardsHighlight", selector: "[data-cards-highlight]" },
	{ load: () => import(/* webpackChunkName: "c-section-artist-discovery" */ "./organisms/sections/section-artist-discovery/section-artist-discovery"), name: "SectionArtistDiscovery", selector: "[data-section-artist-discovery]" },
	{ load: () => import(/* webpackChunkName: "c-articles-list" */ "./organisms/articles-list/articles-list"), name: "ArticlesList", selector: ".articles-list" },
	{ load: () => import(/* webpackChunkName: "c-cards-shop-list" */ "./organisms/cards-shop-list/cards-shop-list"), name: "CardsShopList", selector: ".cards-shop-list" },
	{ load: () => import(/* webpackChunkName: "c-cards-list" */ "./organisms/cards-list/cards-list"), name: "CardsList", selector: "[data-cards-list]" },
	{ load: () => import(/* webpackChunkName: "c-search" */ "./organisms/search/search"), name: "Search", selector: "[data-search]" },
	{ load: () => import(/* webpackChunkName: "c-select" */ "./atoms/inputs/4-input-select/input-select"), name: "Select", selector: "[data-select]" },
	{ load: () => import(/* webpackChunkName: "c-file" */ "./atoms/inputs/7-input-file/input-file"), name: "File", selector: '[data-input-field="file"]' },
	{ load: () => import(/* webpackChunkName: "c-program-filters" */ "./molecules/program-filters/program-filters"), name: "ProgramFilters", selector: "[data-program-filters]" },
	{ load: () => import(/* webpackChunkName: "c-program-widget" */ "./molecules/program-widget/program-widget"), name: "ProgramWidget", selector: "[data-program-widget]" },
	{ load: () => import(/* webpackChunkName: "c-program-card" */ "./molecules/program-card/program-card"), name: "ProgramCard", selector: "[data-program-card]" },
	{ load: () => import(/* webpackChunkName: "c-my-program-accordion" */ "./my-program/my-program-accordion/my-program-accordion"), name: "MyProgramAccordion", selector: "[data-my-program-accordion]" },
	{ load: () => import(/* webpackChunkName: "c-program-stage" */ "./molecules/program-stage/program-stage"), name: "ProgramStage", selector: "[data-program-stage]" },
	{ load: () => import(/* webpackChunkName: "c-program-like-button" */ "./atoms/program-like-button/program-like-button"), name: "ProgramLikeButton", selector: "[data-program-like-button]" },
	{ load: () => import(/* webpackChunkName: "c-my-program-notifications-stack" */ "./my-program/my-program-notifications-stack/my-program-notifications-stack"), name: "MyProgramNotificationsStack", selector: "[data-my-program-notifications-stack]" },
	{ load: () => import(/* webpackChunkName: "c-my-program-auth" */ "./my-program/my-program-auth/my-program-auth"), name: "MyProgramAuth", selector: "[data-my-program-auth]" },
	{ load: () => import(/* webpackChunkName: "c-my-program" */ "./my-program/my-program"), name: "MyProgram", selector: "[data-my-program]" },
	// load after other components
	{ load: () => import(/* webpackChunkName: "c-timer" */ "./molecules/timer/timer"), name: "Timer", selector: "[data-timer]" },
];

// eslint-disable-next-line prefer-const
let LOADED_COMPONENTS = {};

const LOAD_ONCE_COMPONENTS = ["Navigation"];

lineClamp();

function initComponent(View, elements, name) {
	for (let j = 0; j < elements.length; j++) {
		if (name + j in LOADED_COMPONENTS) continue;
		let v;
		try {
			// eslint-disable-next-line new-cap
			v = new View(elements[j], name);
			v.init();
		} catch (error) {
			console.error("Could not init view:", name, "⬇️");
			console.error(error);
		}
		LOADED_COMPONENTS[name + j] = v;
	}
}

export async function componentsLoader() {
	// Synchronous core — run immediately, blocking
	for (const { view, name, selector } of SYNC_COMPONENTS) {
		const elements = document.querySelectorAll(selector);
		if (elements.length) {
			initComponent(view, elements, name);
		}
	}
	lineClamp();

	// Lazy — fire all needed imports in parallel, each initialises when its chunk arrives
	await Promise.all(
		LAZY_COMPONENTS
			.filter(({ selector }) => document.querySelector(selector))
			.map(async ({ load, name, selector }) => {
				const { default: View } = await load();
				const elements = document.querySelectorAll(selector);
				initComponent(View, elements, name);
				lineClamp();
			})
	);

	return LOADED_COMPONENTS;
}

export function componentsDestroy() {
	Object.keys(LOADED_COMPONENTS).forEach((key) => {
		const destroy = !LOAD_ONCE_COMPONENTS.includes(LOADED_COMPONENTS[key].name);
		if (destroy) {
			LOADED_COMPONENTS[key].destroy();
			delete LOADED_COMPONENTS[key];
		}
	});
	return LOADED_COMPONENTS;
}
